// convex/lib/poolFulfillment.ts
//
// Pure poolside-ordering fee + membership arithmetic, shared by the Convex
// server (convex/orders.ts / convex/poolOrders.ts) and the browser cart store
// (src/stores/cart-store.ts). Numbers in, numbers out — no Convex runtime
// imports, no I/O — so both runtimes can import it (mirrors the contract of
// convex/lib/orderTotals.ts). All money values are integer cents unless noted.
//
// The `isMember` boolean is the single lever that applies every Cove Club Plus
// perk. It is derived server-side from an active membership; the browser value
// is display-only. See docs/plan: fee logic is membership-ready even before the
// paid tier ships (isMember simply defaults to false).

/** How a poolside order reaches the guest. */
export type PoolFulfillment = "window" | "runner";

/** Menu categories that are alcohol for age-gate / ID-check purposes. */
export const ALCOHOL_CATEGORIES = ["wine", "cocktails", "spirits", "beer"] as const;

/**
 * Whether a menu item is alcohol. Derived from category so existing menu data
 * needs no backfill; an explicit `isAlcohol` flag (once populated) overrides so
 * N/A cocktails in the `cocktails` category can opt out and spiked items in a
 * food category can opt in.
 */
export function isAlcoholItem(item: {
  category?: string | null;
  isAlcohol?: boolean | null;
}): boolean {
  if (item.isAlcohol === true) return true;
  if (item.isAlcohol === false) return false;
  return (ALCOHOL_CATEGORIES as readonly string[]).includes(item.category ?? "");
}

/**
 * Pricing knobs for the pool channel. Stored as a JSON blob under the
 * `pool_order_settings` key (merged over these defaults exactly like
 * convex/orderSettings.ts merges `order_settings`).
 */
export interface PoolFeeSettings {
  /** Standard runner delivery fee for non-members (cents). */
  poolDeliveryFeeCents: number;
  /** Non-member subtotal at/above which the delivery fee is waived (cents). */
  freeDeliveryThresholdCents: number;
  /** Member subtotal at/above which runner delivery is free (cents). */
  memberFreeDeliveryMinCents: number;
  /** Service-fee rate on the subtotal for non-members (e.g. 0.10 = 10%). */
  serviceFeeRate: number;
  /** Reduced service-fee rate for members (e.g. 0.05 = 5%). */
  memberServiceFeeRate: number;
  /** Cap on the service fee regardless of rate (cents). */
  serviceFeeCapCents: number;
  /** Welcome discount percent applied to a member's first N orders. */
  welcomeDiscountPercent: number;
  /** Number of a member's initial orders that receive the welcome discount. */
  welcomeDiscountOrderLimit: number;
  /** Credit-back rate on member window-pickup orders (e.g. 0.05 = 5%). */
  pickupCreditRate: number;
}

export const DEFAULT_POOL_FEE_SETTINGS: PoolFeeSettings = {
  poolDeliveryFeeCents: 399, // $3.99
  freeDeliveryThresholdCents: 5000, // $50 non-member waiver
  memberFreeDeliveryMinCents: 1500, // $15 member minimum for free delivery
  serviceFeeRate: 0.1, // 10%
  memberServiceFeeRate: 0.05, // 5%
  serviceFeeCapCents: 400, // $4.00 cap
  welcomeDiscountPercent: 20, // 20% off first 3
  welcomeDiscountOrderLimit: 3,
  pickupCreditRate: 0.05, // 5% credit back on member pickup
};

/**
 * Resolve the delivery + service fees for a poolside order.
 *
 * - Window pickup never carries a delivery fee.
 * - Runner delivery is free for members above `memberFreeDeliveryMinCents`,
 *   free for anyone at/above `freeDeliveryThresholdCents` ($50), else the flat
 *   `poolDeliveryFeeCents` ($3.99).
 * - The service fee is `serviceFeeRate` of the subtotal (reduced for members),
 *   clamped to `serviceFeeCapCents`.
 */
export function resolvePoolFees(args: {
  poolFulfillment: PoolFulfillment;
  subtotal: number;
  isMember: boolean;
  settings: PoolFeeSettings;
}): { deliveryFee: number; serviceFee: number } {
  const { poolFulfillment, subtotal, isMember, settings } = args;

  const rate = isMember ? settings.memberServiceFeeRate : settings.serviceFeeRate;
  const serviceFee = Math.min(
    Math.round(Math.max(0, subtotal) * rate),
    settings.serviceFeeCapCents,
  );

  let deliveryFee = 0;
  if (poolFulfillment === "runner") {
    const freeForMember =
      isMember && subtotal >= settings.memberFreeDeliveryMinCents;
    const freeBySubtotal = subtotal >= settings.freeDeliveryThresholdCents;
    deliveryFee = freeForMember || freeBySubtotal ? 0 : settings.poolDeliveryFeeCents;
  }

  return { deliveryFee, serviceFee };
}

/**
 * Welcome discount (cents) for a member's first `welcomeDiscountOrderLimit`
 * orders. `welcomeOrdersRemaining` is read from the member's record; 0 for
 * non-members or once the allowance is used up.
 */
export function resolveMemberWelcomeDiscount(args: {
  isMember: boolean;
  welcomeOrdersRemaining: number;
  subtotal: number;
  settings: PoolFeeSettings;
}): number {
  const { isMember, welcomeOrdersRemaining, subtotal, settings } = args;
  if (!isMember || welcomeOrdersRemaining <= 0) return 0;
  return Math.round(
    Math.max(0, subtotal) * (settings.welcomeDiscountPercent / 100),
  );
}

/**
 * Credit (cents) a member earns back on a completed window-pickup order —
 * awarded post-payment into the rewards ledger to drive the re-order loop.
 * Runner and non-member orders earn nothing here.
 */
export function postPaymentPickupCredit(args: {
  isMember: boolean;
  poolFulfillment: PoolFulfillment;
  total: number;
  settings: PoolFeeSettings;
}): number {
  const { isMember, poolFulfillment, total, settings } = args;
  if (!isMember || poolFulfillment !== "window") return 0;
  return Math.round(Math.max(0, total) * settings.pickupCreditRate);
}

/** Item categories that degrade fastest in Vegas heat (prioritized on delivery). */
export const PERISHABLE_CATEGORIES = ["cocktails", "desserts"] as const;

export function isPerishableItem(item: {
  category?: string | null;
}): boolean {
  return (PERISHABLE_CATEGORIES as readonly string[]).includes(
    item.category ?? "",
  );
}

/**
 * Nevada compliance gate: an alcohol order may not be marked delivered/completed
 * until staff have verified the recipient's ID. Returns true when a transition
 * to `completed` must be blocked. Pure so it can be unit-tested and shared by the
 * status-transition mutation.
 */
export function alcoholCompletionBlocked(
  order: {
    channel?: string | null;
    containsAlcohol?: boolean | null;
    idCheck?: { status?: string | null } | null;
  },
  targetStatus: string,
): boolean {
  if (targetStatus !== "completed") return false;
  if (order.channel !== "pool") return false;
  if (!order.containsAlcohol) return false;
  return order.idCheck?.status !== "verified";
}

export function assertAlcoholCompletionAllowed(
  order: {
    channel?: string | null;
    containsAlcohol?: boolean | null;
    idCheck?: { status?: string | null } | null;
  },
  targetStatus: string,
): void {
  if (!alcoholCompletionBlocked(order, targetStatus)) return;
  throw new Error(
    "Verify the customer's ID before marking this alcohol order delivered.",
  );
}
